import { useEffect, useState, useCallback } from 'react';
import { performanceAPI, type SiteOtdMissResponse, type SiteOtdMissLine } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

interface OtdMissModalProps {
  open: boolean;
  onClose: () => void;
  siteId: number;
  /** Month start 'YYYY-MM-DD'. */
  month: string;
  monthLabel: string;
  /** Set for a customer cell; omit/null for the site-wide "X late" view. */
  customerId?: number | null;
  customerName?: string | null;
  /** Called after any successful override so the parent can refresh its data. */
  onChanged?: () => void;
}

/**
 * Shows the late deliveries behind a sub-100 OTD score (site-month, optionally
 * one customer) and lets a supplier admin override an individual line's OTD
 * verdict (mark on-time / revert). Each override recomputes the cell server-side
 * and the modal re-renders the refreshed misses + summary.
 */
export default function OtdMissModal({
  open, onClose, siteId, month, monthLabel, customerId, customerName, onChanged,
}: OtdMissModalProps) {
  const { formatDate } = useFormatters();
  const [data, setData] = useState<SiteOtdMissResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyLine, setBusyLine] = useState<number | null>(null);
  // Line currently being marked on-time — reveals an inline reason input first.
  const [pending, setPending] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    performanceAPI
      .getSiteOtdMisses({ site: siteId, month, customer: customerId ?? undefined })
      .then((resp) => setData(resp.data))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load OTD detail';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [open, siteId, month, customerId]);

  useEffect(() => { load(); }, [load]);

  const override = async (line: SiteOtdMissLine, makeOnTime: boolean, reasonText = '') => {
    setBusyLine(line.line_id);
    try {
      const resp = await performanceAPI.overrideOtdLine({
        line_id: line.line_id,
        overridden: makeOnTime,
        override_value: makeOnTime ? true : null,
        reason: reasonText.trim() || undefined,
        customer: customerId ?? null,
      });
      setData(resp.data);
      setPending(null);
      setReason('');
      toast.success(makeOnTime ? 'Marked on-time — OTD recalculated' : 'Override removed');
      onChanged?.();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      toast.error(ax?.response?.data?.detail || 'Could not update the override');
    } finally {
      setBusyLine(null);
    }
  };

  const s = data?.summary;
  const canOverride = data?.can_override !== false;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            What caused the OTD miss · {monthLabel}
            {customerName ? ` · ${customerName}` : ''}
          </DialogTitle>
          <DialogDescription>
            {s ? (
              <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span className="font-semibold text-slate-900">
                  OTD {s.otd_pct === null ? '—' : `${s.otd_pct}%`}
                </span>
                <span>{s.measurable} measurable</span>
                <span className="text-emerald-700">{s.on_time + s.early} on-time</span>
                <span className="text-red-700">{s.late} late</span>
              </span>
            ) : 'Late deliveries in this period.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 py-8 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        ) : !data || data.lines.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No late deliveries to show.
          </p>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-white border-b">
                <tr className="text-left text-slate-500">
                  <th className="py-2 pr-3 font-medium">PO</th>
                  <th className="py-2 pr-3 font-medium">Part</th>
                  {!customerId && <th className="py-2 pr-3 font-medium">Customer</th>}
                  <th className="py-2 pr-3 font-medium">Promised</th>
                  <th className="py-2 pr-3 font-medium">Dispatched</th>
                  <th className="py-2 pr-3 font-medium text-right">Days late</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">Override</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l) => {
                  const onTimeByOverride = l.overridden && l.override_value === true;
                  return (
                    <tr key={l.line_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-1.5 pr-3 font-medium">{l.po_number || '—'}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{l.part_number || '—'}</td>
                      {!customerId && <td className="py-1.5 pr-3 text-slate-600 truncate max-w-[160px]">{l.customer_name || '—'}</td>}
                      <td className="py-1.5 pr-3">{l.promised_date ? formatDate(l.promised_date) : '—'}</td>
                      <td className="py-1.5 pr-3">{l.dispatched_date ? formatDate(l.dispatched_date) : '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {l.days_late === null ? '—' : l.days_late}
                      </td>
                      <td className="py-1.5 pr-3">
                        {onTimeByOverride ? (
                          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">On-time (override)</Badge>
                        ) : l.verdict === 'late' ? (
                          <Badge variant="secondary" className="bg-red-50 text-red-700">Late</Badge>
                        ) : (
                          <Badge variant="secondary">{l.verdict}</Badge>
                        )}
                      </td>
                      <td className="py-1.5 text-right">
                        {!canOverride ? (
                          <span className="text-[11px] text-muted-foreground">disabled</span>
                        ) : pending === l.line_id ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <input
                              autoFocus
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && reason.trim()) override(l, true, reason);
                                if (e.key === 'Escape') { setPending(null); setReason(''); }
                              }}
                              placeholder="Reason (required)"
                              className="h-7 w-40 rounded-md border border-slate-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300"
                            />
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                              disabled={busyLine === l.line_id || !reason.trim()}
                              onClick={() => override(l, true, reason)}>
                              {busyLine === l.line_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-1.5 text-xs"
                              onClick={() => { setPending(null); setReason(''); }}>
                              Cancel
                            </Button>
                          </div>
                        ) : l.overridden ? (
                          <div className="flex items-center justify-end gap-1.5">
                            {l.override_reason && (
                              <span className="text-[11px] text-muted-foreground italic truncate max-w-[160px]" title={l.override_reason}>
                                “{l.override_reason}”
                              </span>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              disabled={busyLine === l.line_id}
                              onClick={() => override(l, false)}>
                              {busyLine === l.line_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" /> Revert</>}
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                            disabled={busyLine === l.line_id}
                            onClick={() => { setPending(l.line_id); setReason(''); }}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Mark on-time
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
